import { useState, useCallback } from "react";
import { useProfileMutation, normalizeUserData } from "@core/features/profile";
import type { RawUserData } from "@core/features/profile";
import { usePlatformStore } from "@platform/store";
import type { User } from "@platform/types/platform.types";
import { WebsiteSubStep } from "./WebsiteSubStep";

export type Props = {
  /** Program ID */
  programId: number;
  /** Handler for next button (called after successful save) */
  onNext: () => void;
  /** Handler for back button (hidden if undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total steps */
  totalSteps: number;
};

/**
 * Container for WebsiteSubStep that handles data fetching and mutations.
 */
export function WebsiteSubStepContainer({
  programId: _programId,
  onNext,
  onBack,
  onSkip,
  stepIndex,
  totalSteps,
}: Props) {
  const user = usePlatformStore((s) => s.user);
  const setUser = usePlatformStore((s) => s.setUser);

  const [website, setWebsite] = useState((user as unknown as { website?: string })?.website || "");
  const [_isSubmitting, setIsSubmitting] = useState(false);

  const normalizedUser = user
    ? normalizeUserData(user as unknown as RawUserData)
    : null;

  const { mutateAsync } = useProfileMutation({
    userRole: user?.role || "",
    previousValues: normalizedUser,
    onUserUpdate: (userData) => {
      setUser(userData as User);
    },
  });

  const handleContinue = useCallback(async () => {
    if (!normalizedUser) return;

    setIsSubmitting(true);
    try {
      await mutateAsync({
        ...normalizedUser,
        timezone: normalizedUser.timezone ? [normalizedUser.timezone] : [],
        website: website || undefined,
      } as Parameters<typeof mutateAsync>[0]);
      onNext();
    } catch (error) {
      console.error("Failed to save website:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [website, normalizedUser, mutateAsync, onNext]);

  const handleWebsiteChange = useCallback((value: string) => {
    setWebsite(value);
  }, []);

  return (
    <WebsiteSubStep
      website={website}
      onWebsiteChange={handleWebsiteChange}
      onContinue={handleContinue}
      onBack={onBack}
      onSkip={onSkip}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
    />
  );
}

export default WebsiteSubStepContainer;
